import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import * as styles from './InternalTopbar.module.scss';
import SearchBar, { useSearchBar } from './SearchBar';
import TopbarCreatePostButton from './TopbarCreatePostButton';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import IconButton from '../components/IconButton';
import SVGIcon from '../components/SVGIcon';
import { gql } from '../graphql/generated/gql';
import { PlanTier } from '../graphql/generated/graphql';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import sidebarToggleIcon from '../icons/non-prefilled/16x16/sidebarToggle.svg';
import NotificationCountBadge from '../notification/NotificationCountBadge';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import { UIActions } from '../ui/uiSlice';
import useSlug from '../utils/router/useSlug';
import { openPricingModal } from '../utils/url/pricingPath';
import useShortcut from '../utils/useShortcut';

const FULL_SEARCH_CLASSNAME = styles.fullSearch;

const InnerContext = React.createContext<{
  setIsShowingMobileFullSearch: React.Dispatch<React.SetStateAction<boolean>>;
  internalTopbarRef: React.RefObject<HTMLDivElement>;
}>({
  setIsShowingMobileFullSearch: () => {},
  internalTopbarRef: React.createRef(),
});

const UNREAD_NOTIFICATIONS_QUERY = gql(/** @complexity 4 */ `
  query unreadNotifications {
    session: currentSession {
      organization {
        id
        unreadNotifications(first: 0) {
          totalCount
        }
      }
    }
  }
`);

interface ContainerProps {
  children: React.ReactNode;
}

const Container = ({ children }: ContainerProps) => {
  const internalTopbarRef = useRef<HTMLDivElement | null>(null);
  const [isShowingMobileFullSearch, setIsShowingMobileFullSearch] =
    useState(false);

  const value = useMemo(
    () => ({ setIsShowingMobileFullSearch, internalTopbarRef }),
    [setIsShowingMobileFullSearch],
  );

  return (
    <InnerContext.Provider value={value}>
      <PageLayout.Topbar
        ref={internalTopbarRef}
        className={classNames(styles.internalTopbar, {
          [FULL_SEARCH_CLASSNAME]: isShowingMobileFullSearch,
        })}
      >
        {children}
      </PageLayout.Topbar>
    </InnerContext.Provider>
  );
};

const SidebarToggle = () => {
  const dispatch = useAppDispatch();
  const isSidebarOpen = useIsSidebarOpen();

  const { data: unreadNotificationData } = useQuery(
    UNREAD_NOTIFICATIONS_QUERY,
    {
      skip: isSidebarOpen,
    },
  );

  const unreadNotificationsCount =
    unreadNotificationData?.session?.organization.unreadNotifications
      ?.totalCount ?? 0;

  return (
    <div className={styles.sidebarToggle}>
      <IconButton
        onClick={() => {
          dispatch(UIActions.toggleSidebar());
        }}
        aria-label="Toggle toolbar"
      >
        <SVGIcon size="large">{sidebarToggleIcon}</SVGIcon>
        {!isSidebarOpen && (
          <div className={styles.notificationButtonWrapper}>
            <NotificationCountBadge count={unreadNotificationsCount} />
          </div>
        )}
      </IconButton>
    </div>
  );
};

interface ContainerProps {
  children: React.ReactNode;
}

const SearchBarContainer = ({ children }: ContainerProps) => {
  const { isExpanded } = useSearchBar();

  return (
    <div
      className={classNames(styles.searchContainer, {
        [styles.isExpanded]: isExpanded,
      })}
    >
      {children}
    </div>
  );
};

const Search = () => {
  const { setIsShowingMobileFullSearch, internalTopbarRef } =
    useContext(InnerContext);
  const [isQuickJumpSearch, setIsQuickJumpSearch] = useState(false);
  const searchInputRef = useRef<{ focus(): void } | null>(null);

  const showAndFocusSearch = (quickJump = false) => {
    setIsShowingMobileFullSearch(true);
    setIsQuickJumpSearch(quickJump);

    // Manipulate classes directly to allow auto-focus on mobile Safari.
    internalTopbarRef.current?.classList.add(FULL_SEARCH_CLASSNAME);
    searchInputRef.current?.focus();
  };

  useShortcut('/', (e) => {
    e.preventDefault();
    showAndFocusSearch();
  });

  useShortcut('mod+k', (e) => {
    e.preventDefault();
    showAndFocusSearch(true);
  });

  return (
    <>
      <SearchBar.Root
        onExpandedChange={(expanded) => {
          if (!expanded) {
            setIsQuickJumpSearch(false);
          }
        }}
      >
        <SearchBarContainer>
          <div className={styles.searchInputContainer}>
            <SearchBar.Input
              ref={searchInputRef}
              quickJump={isQuickJumpSearch}
            />
          </div>
          <div className={styles.mobileSearchCancel}>
            <Button onClick={() => setIsShowingMobileFullSearch(false)}>
              Cancel
            </Button>
          </div>
        </SearchBarContainer>
      </SearchBar.Root>
      <div className={styles.mobileSearchButton}>
        <IconButton
          onClick={() => showAndFocusSearch()}
          aria-label="Show search"
        >
          <SVGIcon size="large">{searchIcon}</SVGIcon>
        </IconButton>
      </div>
    </>
  );
};

interface DesktopActionsProps {
  postActions?: React.ReactNode;
}

const DesktopActions = ({ postActions }: DesktopActionsProps) => {
  const [postId] = useSlug('postSlug');
  const [topicId] = useSlug('topicSlug');

  const dispatch = useAppDispatch();
  const location = useLocation();

  const isAdmin =
    useAppSelector((state) => state.session.user).type === 'ADMIN';
  const planTier = useAppSelector(
    (state) => state.session.organization.planTier,
  );

  return (
    <div className={styles.actions}>
      {isAdmin && planTier === PlanTier.Free && location.pathname === '/' && (
        <div className={styles.upgradeText}>
          <span className={styles.upgradeTextInfo}>
            Collaborate with unlimited people.{' '}
          </span>
          <Button
            variant="link"
            onClick={() => openPricingModal(dispatch, 'users')}
          >
            Upgrade now
          </Button>
        </div>
      )}
      {postId ? postActions : <TopbarCreatePostButton topicId={topicId} />}
    </div>
  );
};

interface MobileActionsProps {
  postActions?: React.ReactNode;
}

const MobileActions = ({ postActions }: MobileActionsProps) => {
  const [postId] = useSlug('postSlug');
  const [topicId] = useSlug('topicSlug');

  return (
    <div className={classNames(styles.actions, styles.mobile)}>
      {postId ? postActions : <TopbarCreatePostButton topicId={topicId} />}
    </div>
  );
};

interface InternalTopbarProps {
  postActions?: React.ReactNode;
}

const InternalTopbar = ({ postActions }: InternalTopbarProps) => {
  const isMobileLayout = useIsLayoutSize(LayoutSize.mobile);

  return (
    <Container>
      <SidebarToggle />
      <Search />
      {isMobileLayout ? (
        <MobileActions postActions={postActions} />
      ) : (
        <DesktopActions postActions={postActions} />
      )}
    </Container>
  );
};

export default InternalTopbar;
